export type PipelineStripboardSceneStructure = {
  id: string;
  sceneId: string;
  sceneSequence: number;
  heading: string;
  castIds: string;
  pages: string;
};

export type PipelineStripboardDayStructure = {
  type: "day";
  dayId: string;
};

export type PipelineStripboardRowStructure =
  | PipelineStripboardSceneStructure
  | PipelineStripboardDayStructure;

export const PIPELINE_STRIPBOARD_ROW_STRUCTURES: Record<
  string,
  PipelineStripboardRowStructure
> = {
  "day-01": { type: "day", dayId: "day-01" },
  "scene-1": {
    id: "scene-1",
    sceneId: "scene-1",
    sceneSequence: 1,
    heading: "INT. HOSPITAL ROOM - DAY",
    castIds: "MR",
    pages: "1",
  },
  "scene-2": {
    id: "scene-2",
    sceneId: "scene-2",
    sceneSequence: 2,
    heading: "EXT. GARAGE - NIGHT",
    castIds: "MR",
    pages: "¾",
  },
  "scene-3": {
    id: "scene-3",
    sceneId: "scene-3",
    sceneSequence: 3,
    heading: "EXT. HIGHWAY - DUSK",
    castIds: "MR",
    pages: "1½",
  },
  "day-02": { type: "day", dayId: "day-02" },
  "scene-4": {
    id: "scene-4",
    sceneId: "scene-4",
    sceneSequence: 4,
    heading: "EXT. LOT - SUNSET",
    castIds: "MR",
    pages: "1½",
  },
  "scene-5": {
    id: "scene-5",
    sceneId: "scene-5",
    sceneSequence: 5,
    heading: "INT. LOBBY - DAY",
    castIds: "EP",
    pages: "1",
  },
  "scene-6": {
    id: "scene-6",
    sceneId: "scene-6",
    sceneSequence: 6,
    heading: "INT. POLICE STATION - DAY",
    castIds: "MR",
    pages: "1¼",
  },
  "scene-7": {
    id: "scene-7",
    sceneId: "scene-7",
    sceneSequence: 7,
    heading: "INT. OFFICE - DAY",
    castIds: "MR · EP",
    pages: "1",
  },
  "day-03": { type: "day", dayId: "day-03" },
  "scene-8": {
    id: "scene-8",
    sceneId: "scene-8",
    sceneSequence: 8,
    heading: "EXT. ROOFTOP - NIGHT",
    castIds: "MR",
    pages: "1",
  },
  "scene-9": {
    id: "scene-9",
    sceneId: "scene-9",
    sceneSequence: 9,
    heading: "INT. MARCUS APT. - DAY",
    castIds: "MR",
    pages: "¾",
  },
  "scene-10": {
    id: "scene-10",
    sceneId: "scene-10",
    sceneSequence: 10,
    heading: "EXT. DOCKS - NIGHT",
    castIds: "MR",
    pages: "1½",
  },
  "day-04": { type: "day", dayId: "day-04" },
  "scene-11": {
    id: "scene-11",
    sceneId: "scene-11",
    sceneSequence: 11,
    heading: "EXT. ALLEY - DAY",
    castIds: "MR",
    pages: "½",
  },
  "scene-12": {
    id: "scene-12",
    sceneId: "scene-12",
    sceneSequence: 12,
    heading: "INT. WAREHOUSE - NIGHT",
    castIds: "MR · EP",
    pages: "2⅛",
  },
  "scene-13": {
    id: "scene-13",
    sceneId: "scene-13",
    sceneSequence: 13,
    heading: "INT. CATWALK - CONTINUOUS",
    castIds: "MR · EP",
    pages: "1¾",
  },
  "day-05": { type: "day", dayId: "day-05" },
  "scene-14": {
    id: "scene-14",
    sceneId: "scene-14",
    sceneSequence: 14,
    heading: "EXT. RIVER BANK - DAWN",
    castIds: "MR · EP",
    pages: "1",
  },
};

export type PipelineDemoElementStructure = {
  id: string;
  deptId: string;
  color: string;
  highlightMatch?: string;
};

export const PIPELINE_DEMO_ELEMENT_STRUCTURES: PipelineDemoElementStructure[] =
  [
    {
      id: "marcus",
      deptId: "cast",
      color: "#FFC539",
      highlightMatch: "Marcus",
    },
    {
      id: "warehouse",
      deptId: "set",
      color: "#64748B",
      highlightMatch: "stacks",
    },
    { id: "rain", deptId: "vfx", color: "#34D399" },
    { id: "handgun", deptId: "props", color: "#A78BFA" },
    {
      id: "catwalk",
      deptId: "stunts",
      color: "#5EA8FF",
      highlightMatch: "CATWALK",
    },
  ];

export type PipelineCoverageRubricStructure = {
  id: string;
  score: 1 | 2 | 3 | 4;
};

export const PIPELINE_COVERAGE_RUBRIC_STRUCTURES: PipelineCoverageRubricStructure[] =
  [
    { id: "premise", score: 3 },
    { id: "plot", score: 2 },
    { id: "production_value", score: 1 },
    { id: "characters", score: 3 },
    { id: "setting", score: 3 },
    { id: "structure", score: 4 },
    { id: "casting_potential", score: 3 },
  ];

export type PipelineBudgetCategoryStructure = {
  id: string;
  estimate: number;
  bandClass: string;
  subLineIds?: string[];
  subLineEstimates: Record<string, number>;
};

export const PIPELINE_BUDGET_CATEGORY_STRUCTURES: PipelineBudgetCategoryStructure[] =
  [
    {
      id: "atl",
      estimate: 1_350_000,
      bandClass: "bg-[#2f80ed7a]",
      subLineIds: ["marcus-lead", "elena-lead", "director-fees"],
      subLineEstimates: {
        "marcus-lead": 620_000,
        "elena-lead": 480_000,
        "director-fees": 250_000,
      },
    },
    {
      id: "production",
      estimate: 1_180_000,
      bandClass: "bg-[#9b51e07a]",
      subLineIds: ["camera-electric", "grip-rigging", "production-staff"],
      subLineEstimates: {
        "camera-electric": 520_000,
        "grip-rigging": 310_000,
        "production-staff": 350_000,
      },
    },
    {
      id: "locations",
      estimate: 985_000,
      bandClass: "bg-[#27ae607a]",
      subLineIds: [
        "warehouse-stage",
        "waterfront-docks",
        "hospital-office-police",
      ],
      subLineEstimates: {
        "warehouse-stage": 420_000,
        "waterfront-docks": 185_000,
        "hospital-office-police": 380_000,
      },
    },
    {
      id: "vfx",
      estimate: 340_000,
      bandClass: "bg-[#ff471a7a]",
      subLineIds: ["rain-fx", "catwalk-rig", "comp-cleanup"],
      subLineEstimates: {
        "rain-fx": 210_000,
        "catwalk-rig": 85_000,
        "comp-cleanup": 45_000,
      },
    },
    {
      id: "post",
      estimate: 495_000,
      bandClass: "bg-[#2f80ed7a]",
      subLineIds: ["editorial", "sound-mix", "color-deliverables"],
      subLineEstimates: {
        editorial: 220_000,
        "sound-mix": 165_000,
        "color-deliverables": 110_000,
      },
    },
    {
      id: "contingency",
      estimate: 435_000,
      bandClass: "bg-white/[0.12]",
      subLineEstimates: {},
    },
  ];

export type PipelineBudgetGuildStructure = {
  id: string;
  enabled: boolean;
};

export const PIPELINE_BUDGET_GUILD_STRUCTURES: PipelineBudgetGuildStructure[] =
  [
    { id: "dga", enabled: true },
    { id: "wga", enabled: false },
    { id: "sag", enabled: true },
    { id: "iatse", enabled: true },
    { id: "teamsters", enabled: false },
  ];
